// Shared helpers for the /models catalog page (and any future surface
// that wants the same chip / tag / capability vocabulary).
//
// Kept deliberately framework-free — no React, no theme — so it can be
// imported by any component (Card, ListRow, Rail) without dragging
// rendering logic with it.

// Output capability palette. Used for output-kind chips so a video
// model reads as the same pink in the card grid, the list row, and the
// active-filter chip.
export const CAPABILITY_COLORS = {
  text:  '#3B82F6',
  image: '#22C55E',
  video: '#EC4899',
  audio: '#8B5CF6',
  '3d':  '#F59E0B',
};

// Tag chip / accent colors are derived from the shared `colorForSlug` hash
// in `agent_catalog.js`, so a tag, agent category, tool category, and task
// category that share a slug all read the same color across the app.
export {
  colorForSlug,
  hexToRgba,
  paletteForSlug,
  heroChromaForSlug,
  hoverSurfaceForSlug,
  breadcrumbHoverTintForSlug,
} from '@/utils/agent_catalog';

// Output-kind chips, ordered the way they should appear on a row.
// Falls back to "Text" when a model literally claims no outputs (rare).
export const getOutputChips = (model) => {
  const out = model?.capabilities?.output || {};
  const chips = [];
  if (out.hasImages) chips.push({ key: 'image', label: 'Image', icon: 'image',      color: CAPABILITY_COLORS.image });
  if (out.hasVideo)  chips.push({ key: 'video', label: 'Video', icon: 'video',      color: CAPABILITY_COLORS.video });
  if (out.hasAudio)  chips.push({ key: 'audio', label: 'Audio', icon: 'music',      color: CAPABILITY_COLORS.audio });
  if (out.has3D)     chips.push({ key: '3d',    label: '3D',    icon: 'cube',       color: CAPABILITY_COLORS['3d'] });
  if (out.hasText && chips.length === 0)
                     chips.push({ key: 'text',  label: 'Text',  icon: 'align-left', color: CAPABILITY_COLORS.text });
  return chips;
};

// Primary output kind (used for card accent color + grouping).
export const getPrimaryOutput = (model) => {
  const chips = getOutputChips(model);
  return chips[0] || { key: 'text', label: 'Text', icon: 'align-left', color: CAPABILITY_COLORS.text };
};

// Input-kind chips. Used by the list view's "Inputs" cell.
export const getInputChips = (model) => {
  const inp = model?.capabilities?.input || {};
  const chips = [];
  if (inp.hasTextSupport)  chips.push({ key: 'text',  label: 'Text',  icon: 'align-left' });
  if (inp.hasImageSupport) chips.push({ key: 'image', label: 'Image', icon: 'image' });
  if (inp.hasAudioSupport) chips.push({ key: 'audio', label: 'Audio', icon: 'music' });
  if (inp.hasVideoSupport) chips.push({ key: 'video', label: 'Video', icon: 'video' });
  return chips;
};

// "New" badge rule — same 7-day window the model selector uses.
export const isNewModel = (model) => {
  if (!model?.createdAt) return false;
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  return new Date(model.createdAt) > sevenDaysAgo;
};

// Loose local-only filter for instant typing feedback while the server
// search debounces. The server result eventually replaces this.
export const filterModels = (models, query) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) return models;
  return models.filter(m =>
    (m.name || '').toLowerCase().includes(q) ||
    (m.description || '').toLowerCase().includes(q) ||
    (m.tags || []).some(t => (t.name || t.slug || '').toLowerCase().includes(q)),
  );
};

// Stable sort comparator used by the catalog toolbar. Caller supplies
// a `getCompanyName` resolver so this stays store-agnostic.
export const sortModels = (models, sortKey, { getCompanyName } = {}) => {
  return [...models].sort((a, b) => {
    switch (sortKey) {
      case 'newest':
        return (b?.createdAt ? new Date(b.createdAt).getTime() : 0)
             - (a?.createdAt ? new Date(a.createdAt).getTime() : 0);
      case 'name':
        return (a.name || '').localeCompare(b.name || '');
      case 'cost':
        return (a?.creditCosts?.baseCost ?? Infinity)
             - (b?.creditCosts?.baseCost ?? Infinity);
      case 'company':
        return (getCompanyName?.(a.companyId) || '').localeCompare(getCompanyName?.(b.companyId) || '');
      case 'trending':
      default: {
        // Trending = featured + new + popularity, with a stable tie-break
        // on createdAt desc.
        const score = (m) =>
          (m?.flags?.isFeatured ? 100 : 0) +
          (isNewModel(m) ? 50 : 0) +
          (m?.popularityScore ?? 0);
        const sb = score(b);
        const sa = score(a);
        if (sa !== sb) return sb - sa;
        return (b?.createdAt ? new Date(b.createdAt).getTime() : 0)
             - (a?.createdAt ? new Date(a.createdAt).getTime() : 0);
      }
    }
  });
};

// Provider/model display name. Returns a tuple [providerSlug, modelSlug]
// — let the renderer decide how to compose them. Falls back to model
// name when there's no company info, so the cell never reads as
// `/model-name`.
export const providerSlug = (model, company) => {
  const provider = (company?.slug || company?.name || '')
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-');
  const slug = model?.slug || (model?.name || '').toString().toLowerCase().replace(/\s+/g, '-');
  return { provider, slug };
};
